import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Footer from '../Components/Footer';
import Breadcrumbs from '../Components/Breadcrumbs';
import { googleSignInMessage } from '../utils/authMessages';

/**
 * Signup Page - New user registration for Soap Lab
 */
export default function SignupPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);
  
  const { signUp, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!accepted) {
      setError('Please confirm you are 18 or older and agree to the Terms of Use and Privacy Policy.');
      return;
    }

    // Validation
    if (name.trim().length < 2) {
      setError('Please enter your full name.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      await signUp(email, password, name.trim());
      navigate('/verify-email', { replace: true });
    } catch (err) {
      console.error(err);
      if (err.code === 'auth/email-already-in-use') {
        setError('This email already has a SoapLab account. Sign in instead, with “Continue with Google” if that’s how you joined.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please use a stronger password.');
      } else {
        setError('Failed to create account. Please try again.');
      }
    }

    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setError('');
    if (!accepted) {
      setError('Please confirm you are 18 or older and agree to the Terms of Use and Privacy Policy.');
      return;
    }
    setLoading(true);

    try {
      const result = await signInWithGoogle();
      if (result) {
        navigate('/', { replace: true });
      } else {
        setLoading(false);
      }
    } catch (err) {
      setError(googleSignInMessage(err));
      setLoading(false);
    }
  };

  return (
    <div className="sl-page auth-page">
      <div className="pageheader borderleft">
        <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'Sign up' }]} />
        <div className="pageheading">Create your account</div>
      </div>

      <div className="auth-content borderleft">
        <div className="auth-container">
        <p className="auth-subtitle">Free to start. Upgrade only when your business needs more.</p>

        {/* Error Message */}
        {error && (
          <div className="auth-error">
            <span>⚠️</span> {error}
          </div>
        )}

        {/* Explicit acceptance covers both email and Google sign-up. */}
        <label className="auth-accept">
          <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
          <span>
            I am 18 or older and agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Use</a>, including the soapmaking safety section, and the{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
          </span>
        </label>

        {/* Google Sign In */}
        <button 
          onClick={handleGoogleSignIn} 
          className="auth-google-btn"
          disabled={loading}
        >
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        <div className="auth-divider">
          <span>or</span>
        </div>

        {/* Signup Form */}
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="auth-form-grid">
          <div className="form-group">
            <label htmlFor="name">Full Name</label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              minLength={6}
              required
            />
            <span className="form-hint">At least 6 characters</span>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>
          </div>

          <button 
            type="submit" 
            className="auth-submit-btn"
            disabled={loading}
          >
            {loading ? 'Creating account...' : 'Create account'}
          </button>
        </form>
        <p className="auth-switch">
          Already have an account?{' '}
          <Link to="/login">Sign in</Link>
        </p>
        </div>

        <aside className="auth-aside">
          <span className="sl-eyebrow">Free plan</span>
          <h2 className="sl-subtitle">Start free, upgrade later</h2>
          <ul className="auth-aside-list">
            <li>Full lye and melt &amp; pour calculators</li>
            <li>Save up to 10 recipes</li>
            <li>30 ingredients and 5 batch records</li>
            <li>Recipe templates and the community</li>
          </ul>
          <p className="auth-aside-note">No card needed. Pro adds unlimited recipes, labels and sales reports when your business needs them.</p>
        </aside>
      </div>
      <Footer />
    </div>
  );
}
